import React, { useEffect, useRef, useState } from "react";
import "../styles/Skills.css";
import {
  FaLaptopCode, FaServer, FaDatabase,
  FaTools, FaUserFriends, FaLightbulb,
} from "react-icons/fa";

const TECHNICAL = [
  {
    Icon: FaLaptopCode,
    title: "Frontend Development",
    desc: "Modern, responsive user interfaces for websites and web applications.",
    tags: ["HTML", "CSS", "JavaScript", "ReactJS", "Responsive Design"],
  },
  {
    Icon: FaServer,
    title: "Backend Development",
    desc: "Secure, scalable, high-performance server-side applications and APIs.",
    tags: ["Node.js", "Express", "PHP", "WordPress", "REST API"],
  },
  {
    Icon: FaDatabase,
    title: "Database",
    desc: "Robust, secure, and high-performance database systems for web platforms.",
    tags: ["MySQL", "PostgreSQL", "Firebase"],
  },
  {
    Icon: FaTools,
    title: "Tools & Workflow",
    desc: "Modern tooling and optimised workflows for efficient, reliable delivery.",
    tags: ["Git / GitHub", "VS Code", "Deployment", "Testing"],
  },
];

const PROFESSIONAL = [
  {
    Icon: FaLightbulb,
    title: "Business & Branding",
    desc: "Strong brand identities and digital strategies that help businesses grow and stand out.",
    tags: ["Brand Development", "Digital Marketing", "SEO", "Management"],
  },
  {
    Icon: FaTools,
    title: "Tools & Productivity",
    desc: "Productivity platforms to organise work, manage projects, and improve workflow performance.",
    tags: ["Microsoft Office", "Google Workspace", "Notion", "Project Mgmt"],
  },
  {
    Icon: FaUserFriends,
    title: "Soft Skills",
    desc: "Interpersonal and problem-solving strengths that elevate teamwork and project success.",
    tags: ["Communication", "Problem Solving", "Time Management", "Creativity"],
  },
];

/* ── single card with IntersectionObserver ── */
const SkillCard = ({ item, index, accent = "cyan" }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`skill-card skill-card--${accent} ${vis ? "skill-card--visible" : ""}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="skill-card-top">
        <span className="skill-card-icon">
          <item.Icon />
        </span>
        <span className="skill-card-index">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      <h3 className="skill-card-title">{item.title}</h3>
      <p className="skill-card-desc">{item.desc}</p>

      <div className="skill-tags">
        {item.tags.map((t) => (
          <span key={t} className="skill-tag">{t}</span>
        ))}
      </div>

      {/* animated corner accent */}
      <div className="skill-card-corner" aria-hidden="true" />
    </div>
  );
};

/* ── section group header ── */
const GroupHeader = ({ mono, heading }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`skills-group-header ${vis ? "skills-group-header--visible" : ""}`}>
      <span className="section-label">{mono}</span>
      <h3 className="skills-group-title">{heading}</h3>
      <div className="skills-group-rule" />
    </div>
  );
};

const Skills = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="skills" id="skills" ref={ref}>
      <div className="about-rule" aria-hidden="true" />

      <div className="skills-container">
        {/* ── Top header ── */}
        <div className={`skills-header ${vis ? "skills-header--visible" : ""}`}>
          <span className="section-label">// expertise</span>
          <h2 className="section-title">
            What I <em>bring</em><br />to the table
          </h2>
          <div className="divider" />
          <p className="section-subtitle">
            Expert web development powered by modern technologies — creating scalable,
            user-focused digital experiences that help businesses grow and stand out.
          </p>
        </div>

        {/* ── Technical group ── */}
        <GroupHeader mono="// 01 — technical" heading="Technical Skills" />
        <div className="skills-grid">
          {TECHNICAL.map((item, i) => (
            <SkillCard key={item.title} item={item} index={i} accent="cyan" />
          ))}
        </div>

        {/* ── Professional group ── */}
        <GroupHeader mono="// 02 — professional" heading="Professional Skills" />
        <div className="skills-grid skills-grid--3">
          {PROFESSIONAL.map((item, i) => (
            <SkillCard key={item.title} item={item} index={i} accent="gold" />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
